import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PartnerContactCard } from "@/components/hub/PartnerContactCard";
import { ManagersCard } from "@/components/hub/ManagersCard";
import { toast } from "sonner";
import { Loader2, Mail, MessageSquare, Inbox, MoonStar, BellRing, Send, Sunrise, Gauge, Clock3, Zap, AlertTriangle } from "lucide-react";

const CH = [
  { key: "email", label: "Email", Icon: Mail },
  { key: "sms", label: "Text", Icon: MessageSquare },
];

/**
 * DeskNotifications — one page for what reaches the owner's own inbox and phone.
 * Everything not ticked still lands in the Hub; nothing is ever silently dropped.
 */
export default function DeskNotifications() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/hub/desk/notify-prefs");
      setData(r.data);
    } catch { setData({ prefs: {}, events: [] }); }
  };
  useEffect(() => { load(); }, []);

  const set = async (key, value) => {
    setSaving(key);
    setData((d) => ({ ...d, prefs: { ...d.prefs, [key]: value } }));
    try {
      const r = await api.patch("/hub/desk/notify-prefs", { [key]: value });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save that");
      load();
    } finally { setSaving(null); }
  };

  if (!data) {
    return <div className="grid place-items-center py-28 text-slate-400"><Loader2 className="size-6 animate-spin" /></div>;
  }

  const p = data.prefs || {};
  const silent = (k) => !p[`email_${k}`] && !p[`sms_${k}`];
  const offCount = (data.events || []).filter((e) => silent(e.key)).length;

  return (
    <div className="space-y-6 pb-24" data-testid="desk-notifications">
      <div>
        <div className="eyebrow">Your desk</div>
        <h1 className="t">What reaches you</h1>
        <p className="mt-2 max-w-2xl text-[13.5px] text-slate-500">
          Tick what you want in your inbox or on your phone. Untick both and it still happens —
          it just waits for you in the Hub instead of interrupting you.
          {offCount > 0 && <> You currently have <b className="text-slate-700">{offCount}</b> of {data.events.length} set to Hub only.</>}
        </p>
      </div>

      <label className="flex cursor-pointer items-start gap-3 card p-5" data-testid="desk-notify-texts-to-me-row">
        <input type="checkbox" checked={!!p.texts_to_me}
               onChange={(e) => set("texts_to_me", e.target.checked)}
               className="mt-0.5 size-4 accent-[#0055FF]" data-testid="desk-notify-texts-to-me" />
        <span>
          <span className="text-[13.5px] font-medium text-slate-900">Texts to my phone</span>
          <span className="mt-0.5 block text-[12px] text-slate-500" data-testid="desk-notify-texts-to-me-hint">
            {p.texts_to_me
              ? <>On. Alerts below can text {data.my_mobile || "your mobile"}.</>
              : <>Off. Nothing texts {data.my_mobile || "your mobile"}, except Connie answering when you text her. Clients, introducers and lenders still get their texts.</>}
          </span>
        </span>
      </label>

      <div className="flex items-start gap-3 card p-5" data-testid="desk-round-up-note">
        <Sunrise className="mt-0.5 size-4 shrink-0 text-slate-400" />
        <span>
          <span className="text-[13.5px] font-medium text-slate-900">Your own round-up — 8am and 4pm UK</span>
          <span className="mt-0.5 block text-[12px] text-slate-500">
            Notes on cases, document requests, chases and client uploads no longer arrive one email at a time.
            They're held and sent together at 8am and 4pm, and only when something moved. New cases, partner
            messages and deal alerts still reach you straight away.
          </span>
        </span>
      </div>

      <SlaCard />

      <div className="overflow-hidden card">
        <div className="hidden grid-cols-[1fr_88px_88px_112px] gap-2 border-b border-slate-100 bg-slate-50 px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500 lg:grid">
          <span>When this happens</span>
          <span className="text-center">Email</span>
          <span className="text-center">Text</span>
          <span className="text-center">Hub only</span>
        </div>
        {(data.events || []).map((e) => (
          <div key={e.key}
               className="grid grid-cols-1 gap-3 border-b border-slate-100 px-5 py-4 last:border-0 lg:grid-cols-[1fr_88px_88px_112px] lg:items-center"
               data-testid={`desk-notify-row-${e.key}`}>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[13.5px] font-medium text-slate-900">{e.label}</span>
                {e.recommended && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-[#0055FF]/25 bg-[#0055FF]/[0.06] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-[#0044CC]">
                    <BellRing className="size-2.5" /> worth keeping on
                  </span>
                )}
              </div>
              <div className="mt-0.5 text-[12px] text-slate-500">{e.hint}</div>
            </div>
            {CH.map(({ key, label, Icon }) => (
              <label key={key}
                     className="flex cursor-pointer items-center justify-center gap-2 rounded-lg py-1.5 transition-colors hover:bg-slate-50 lg:gap-0">
                <span className="text-[12px] text-slate-500 lg:hidden"><Icon className="mr-1 inline size-3.5" />{label}</span>
                <input type="checkbox"
                       checked={!!p[`${key}_${e.key}`]}
                       disabled={saving === `${key}_${e.key}`}
                       onChange={(ev) => set(`${key}_${e.key}`, ev.target.checked)}
                       className="size-4 accent-[#0055FF]"
                       data-testid={`desk-notify-${key}-${e.key}`} />
              </label>
            ))}
            <div className="flex items-center justify-center">
              {silent(e.key) ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-[10.5px] font-semibold text-slate-600"
                      data-testid={`desk-notify-hubonly-${e.key}`}>
                  <Inbox className="size-3" /> Hub only
                </span>
              ) : (
                <span className="text-[10.5px] text-slate-300">—</span>
              )}
            </div>
          </div>
        ))}
      </div>

      <label className="flex cursor-pointer items-start gap-3 card p-5"
             data-testid="desk-notify-quiet-row">
        <input type="checkbox" checked={!!p.hold_sms_out_of_hours}
               onChange={(e) => set("hold_sms_out_of_hours", e.target.checked)}
               className="mt-0.5 size-4 accent-[#0055FF]" data-testid="desk-notify-quiet" />
        <span>
          <span className="flex items-center gap-2 text-[13.5px] font-medium text-slate-900">
            <MoonStar className="size-4 text-slate-400" /> Hold texts outside desk hours
          </span>
          <span className="mt-0.5 block text-[12px] text-slate-500">
            Nothing buzzes your phone outside {data.quiet_hours_label || "desk hours"}. Emails still
            arrive as normal, and anything held is waiting in the Hub when you open it.
          </span>
        </span>
      </label>

      <MorningBriefCard
        deskInbox={data.desk_inbox}
        testid="morning-brief-card"
        title="The introducers' daily round-up"
        blurb={<>Every introducer gets one email at <b>5pm UK on weekdays</b> listing what moved on their cases
          (Monday's covers the weekend) — and nothing at all on a quiet day. Only offers, funded deals, a lender
          needing something and commission paid reach them instantly, by email and text; each of them can
          switch the round-up off in their own portal.</>}
        previewUrl="/hub/case-digest/preview"
        runUrl="/hub/case-digest/run"
        quietLine="Nothing has moved in the last 24 hours, so today's 5pm round-up is empty as it stands."
        rowName={(r) => r.name}
        rowKey={(r) => r.referrer_id}
        offLabel={(n) => `${n} introducer${n === 1 ? " has" : "s have"} it switched off.`}
      />
      <MorningBriefCard
        deskInbox={data.desk_inbox}
        testid="desk-brief-card"
        title="The desks' daily round-up"
        blurb={<>Every lending desk gets one email at <b>5pm UK on weekdays</b> listing what's waiting on their
          decision, what they're reviewing, offers they have out, notes and paperwork. Only new cases and a
          client's documents reach them instantly.
          <b> This replaced the old "deals awaiting your review" chase</b> — you can still lean on a
          desk by hand from the Overview.</>}
        previewUrl="/hub/desk-brief/preview"
        runUrl="/hub/desk-brief/run"
        quietLine="No desk has anything waiting on it right now, so the next 5pm round-up is empty as it stands."
        rowName={(r) => r.company_name}
        rowKey={(r) => r.lender_id}
        offLabel={(n) => `${n} desk${n === 1 ? " has" : "s have"} it switched off.`}
      />
      <PartnerContactCard />
      <ManagersCard />
      <EmailPreviewCard deskInbox={data.desk_inbox} />
    </div>
  );
}

/** The hard SLA ladder: chase a silent desk, then take the case off it — both the owner's to set. */
function SlaCard() {
  const [s, setS] = useState(null);
  const [sum, setSum] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/hub/settings"), api.get("/hub/sla")]);
      setS(a.data); setSum(b.data);
    } catch { setS({}); setSum(null); }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    setS((d) => ({ ...d, ...patch }));
    try {
      const r = await api.patch("/hub/settings", patch);
      setS(r.data);
      const b = await api.get("/hub/sla");
      setSum(b.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save that");
      load();
    } finally { setSaving(false); }
  };

  if (!s) return null;
  const enabled = s.sla_enabled !== false;
  const chaseH = s.sla_chase_hours ?? 24;
  const pullH = s.sla_pull_hours ?? 48;
  const asDays = (h) => (h % 24 === 0 ? `${h / 24} day${h / 24 === 1 ? "" : "s"}` : `${h}h`);

  const Stat = ({ Icon, n, label, tone }) => (
    <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
      <Icon className="size-4" style={{ color: tone }} />
      <div><div className="text-[15px] font-bold text-slate-900 leading-none">{n}</div>
        <div className="text-[10.5px] text-slate-500 mt-0.5">{label}</div></div>
    </div>
  );

  return (
    <div className="card p-5" data-testid="hub-sla-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[13.5px] font-medium text-slate-900">
            <Gauge className="size-4 text-slate-400" /> Decision SLA — the clock on every desk
          </div>
          <p className="mt-1 max-w-xl text-[12px] text-slate-500">
            A case that goes quiet is what loses the introducer their client. Set how long a desk may
            sit on a case before we chase it, and before we take it back and place it elsewhere.
          </p>
        </div>
        <label className="flex cursor-pointer items-center gap-2 shrink-0">
          <input type="checkbox" checked={enabled} disabled={saving}
                 onChange={(e) => save({ sla_enabled: e.target.checked })}
                 className="size-4 accent-[#0055FF]" data-testid="hub-sla-enabled" />
          <span className="text-[12.5px] font-semibold text-slate-700">{enabled ? "On" : "Off"}</span>
        </label>
      </div>

      {enabled && (
        <>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-[0.14em] text-slate-600 font-semibold">Chase the desk after</span>
              <div className="mt-1.5 flex items-center gap-2">
                <input type="number" min={1} max={720} value={chaseH} disabled={saving}
                       onChange={(e) => setS((d) => ({ ...d, sla_chase_hours: Number(e.target.value) }))}
                       onBlur={(e) => save({ sla_chase_hours: Math.max(1, Number(e.target.value) || 24) })}
                       className="h-9 w-24 rounded-lg border border-slate-300 px-3 text-[13px] text-slate-900"
                       data-testid="hub-sla-chase-hours" />
                <span className="text-[12px] text-slate-500">hours · {asDays(chaseH)} of silence</span>
              </div>
            </label>
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-[0.14em] text-slate-600 font-semibold">Take the case back after</span>
              <div className="mt-1.5 flex items-center gap-2">
                <input type="number" min={2} max={1440} value={pullH} disabled={saving || !s.sla_auto_pull}
                       onChange={(e) => setS((d) => ({ ...d, sla_pull_hours: Number(e.target.value) }))}
                       onBlur={(e) => save({ sla_pull_hours: Math.max(chaseH + 1, Number(e.target.value) || 48) })}
                       className="h-9 w-24 rounded-lg border border-slate-300 px-3 text-[13px] text-slate-900 disabled:opacity-50"
                       data-testid="hub-sla-pull-hours" />
                <span className="text-[12px] text-slate-500">hours · {asDays(pullH)} of silence</span>
              </div>
            </label>
          </div>

          <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3.5">
            <input type="checkbox" checked={s.sla_auto_pull !== false} disabled={saving}
                   onChange={(e) => save({ sla_auto_pull: e.target.checked })}
                   className="mt-0.5 size-4 accent-[#0055FF]" data-testid="hub-sla-auto-pull" />
            <span>
              <span className="text-[13px] font-semibold text-slate-900">Take the case back automatically</span>
              <span className="mt-0.5 block text-[12px] text-slate-500">
                When a desk is still silent at the take-back mark, we withdraw the case from it, tell
                the desk why, and free it to go elsewhere — no one has to watch the clock. Turn this off
                to keep chasing without ever pulling automatically.
              </span>
            </span>
          </label>

          {sum && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="hub-sla-stats">
              <Stat Icon={Clock3} n={sum.on_track} label="on track" tone="#5B86D6" />
              <Stat Icon={Zap} n={sum.chasing} label="being chased" tone="#0055FF" />
              <Stat Icon={AlertTriangle} n={sum.overdue} label="overdue" tone="#0055FF" />
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** One 5pm round-up, previewed and sendable to yourself. Used for both partner sides. */
function MorningBriefCard({ deskInbox, testid, title, blurb, previewUrl, runUrl, quietLine,
                            rowName, rowKey, offLabel }) {
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(previewUrl).then((r) => setData(r.data)).catch(() => setData({ recipients: [] }));
  }, [previewUrl]);

  const sendCopy = async () => {
    if (!deskInbox) { toast.error("No desk inbox set"); return; }
    setSending(true);
    try {
      const r = await api.post(runUrl, { to: deskInbox, force: true });
      if (r.data.sent > 0) toast.success(`${r.data.sent} brief${r.data.sent === 1 ? "" : "s"} sent to ${deskInbox}`);
      else toast.info("Nothing to report right now — so nothing was sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the brief");
    } finally { setSending(false); }
  };

  const ready = data?.ready ?? 0;

  return (
    <div className="card p-5" data-testid={testid}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[13.5px] font-medium text-slate-900">
            <Sunrise className="size-4 text-slate-400" /> {title}
          </div>
          <p className="mt-1 max-w-xl text-[12px] text-slate-500">{blurb}</p>
        </div>
        <button onClick={sendCopy} disabled={sending} data-testid={`${testid}-send`}
                className="inline-flex h-9 shrink-0 items-center gap-2 rounded-xl border border-slate-200 px-3.5 text-[12.5px] font-semibold text-slate-700 transition-colors hover:border-[#0055FF] hover:text-[#0044CC] disabled:opacity-60">
          {sending ? <><Loader2 className="size-3.5 animate-spin" /> Sending…</> : <>Send me a copy</>}
        </button>
      </div>
      {data && (
        <div className="mt-3.5 border-t border-slate-100 pt-3" data-testid={`${testid}-recipients`}>
          {ready === 0 ? (
            <div className="text-[12px] text-slate-500" data-testid={`${testid}-quiet`}>
              {quietLine}
              {data.switched_off > 0 && <> {offLabel(data.switched_off)}</>}
            </div>
          ) : (
            <>
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-600">
                {ready} would get one right now
              </div>
              <div className="mt-1.5 divide-y divide-slate-100">
                {data.recipients.map((r) => (
                  <div key={rowKey(r)} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 py-2"
                       data-testid={`${testid}-row-${rowKey(r)}`}>
                    <span className="min-w-0 text-[13px] font-medium text-slate-900">{rowName(r)}</span>
                    <span className="min-w-0 text-[12px] text-slate-500">{r.headline}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Send every partner email to one inbox, rendered from the live templates. */
function EmailPreviewCard({ deskInbox }) {
  const [to, setTo] = useState(deskInbox || "");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { if (deskInbox) setTo(deskInbox); }, [deskInbox]);

  const send = async () => {
    if (!to.trim()) { toast.error("Which inbox should they go to?"); return; }
    setSending(true);
    setResult(null);
    try {
      const r = await api.post("/admin/preview-partner-emails", { to: to.trim() });
      setResult(r.data);
      toast.success(`${r.data.sent_count} of ${r.data.total} emails sent to ${r.data.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the preview set");
    } finally { setSending(false); }
  };

  return (
    <div className="card p-5" data-testid="email-preview-card">
      <div className="flex items-center gap-2 text-[13.5px] font-medium text-slate-900">
        <Send className="size-4 text-slate-400" /> See every email your partners get
      </div>
      <p className="mt-1 text-[12px] text-slate-500">
        Sends one of each — every application, approval, deal alert, nudge, statement and chat ping —
        rendered from the live templates, so what lands is exactly what a lender or introducer receives.
        Numbered so they sort in reading order.
      </p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <input type="email" value={to} onChange={(e) => setTo(e.target.value)}
               placeholder="ops@connex.is" data-testid="email-preview-to"
               className="h-10 flex-1 rounded-xl border border-slate-200 px-3 text-[13px] text-slate-900 outline-none transition-colors focus:border-[#0055FF]" />
        <button onClick={send} disabled={sending} data-testid="email-preview-send"
                className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-[#0055FF] px-4 text-[13px] font-semibold text-white transition-colors hover:bg-[#0044CC] disabled:opacity-60">
          {sending ? <><Loader2 className="size-4 animate-spin" /> Sending…</> : <>Send me the set</>}
        </button>
      </div>
      {result && (
        <div className="mt-3 text-[12px] text-slate-600" data-testid="email-preview-result">
          {result.sent_count} of {result.total} delivered to <b>{result.to}</b>
          {result.failed_count > 0 && <span className="text-amber-600"> · {result.failed_count} failed</span>}
        </div>
      )}
    </div>
  );
}
